import React from 'react';

interface CheckoutDeviceFrameProps {
  device: 'desktop' | 'tablet' | 'mobile';
  currency: string;
  planTier: string;
  children: React.ReactNode;
}

export const CheckoutDeviceFrame: React.FC<CheckoutDeviceFrameProps> = ({
  device,
  currency,
  planTier,
  children,
}) => {
  if (device === 'mobile') {
    return (
      <div className="w-[375px] max-w-[95vw] h-[720px] max-h-[calc(100vh-140px)] bg-slate-900 rounded-[44px] p-3 shadow-2xl border-4 border-slate-700/80 flex flex-col relative shrink-0 transition-all duration-300">
        {/* Top Mobile Speaker & Dynamic Island */}
        <div className="w-full flex items-center justify-between px-5 pt-1.5 pb-2 text-[11px] font-bold text-slate-300 select-none shrink-0">
          <span>9:41</span>
          <div className="w-24 h-4 bg-slate-950 rounded-full flex items-center justify-center gap-1.5 shadow-inner">
            <span className="w-2 h-2 rounded-full bg-slate-900 border border-slate-800"></span>
            <span className="w-1.5 h-1.5 rounded-full bg-slate-900"></span>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Cellular signal SVG */}
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
              <rect x="2" y="16" width="3" height="6" rx="0.5" />
              <rect x="7" y="12" width="3" height="10" rx="0.5" />
              <rect x="12" y="8" width="3" height="14" rx="0.5" />
              <rect x="17" y="4" width="3" height="18" rx="0.5" />
            </svg>
            {/* Battery SVG */}
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="16" height="10" rx="2" />
              <line x1="20" y1="10" x2="20" y2="14" />
              <rect x="4" y="9" width="10" height="6" rx="1" fill="currentColor" />
            </svg>
          </div>
        </div>

        {/* Mobile Viewport Screen */}
        <div className="flex-1 w-full bg-slate-50 dark:bg-slate-950 rounded-[32px] overflow-hidden flex flex-col relative shadow-inner">
          <div className="flex-1 w-full h-full overflow-hidden">
            {children}
          </div>
        </div>

        {/* Bottom Home Indicator Bar */}
        <div className="pt-2 pb-0.5 flex justify-center shrink-0">
          <div className="w-28 h-1 bg-slate-400/50 rounded-full"></div>
        </div>
      </div>
    );
  }

  if (device === 'tablet') {
    return (
      <div className="w-[768px] max-w-[96vw] h-[760px] max-h-[calc(100vh-140px)] bg-slate-900 rounded-[32px] p-3.5 shadow-2xl border-4 border-slate-700/80 flex flex-col relative shrink-0 transition-all duration-300">
        {/* Tablet Top Camera dot */}
        <div className="w-full flex items-center justify-center pb-2 shrink-0">
          <div className="w-2.5 h-2.5 bg-slate-950 rounded-full border border-slate-800 shadow-inner"></div>
        </div>

        {/* Tablet Viewport Screen */}
        <div className="flex-1 w-full bg-slate-50 dark:bg-slate-950 rounded-[22px] overflow-hidden flex flex-col relative shadow-inner">
          <div className="flex-1 w-full h-full overflow-hidden">
            {children}
          </div>
        </div>

        {/* Bottom Home Bar */}
        <div className="pt-2.5 pb-0.5 flex justify-center shrink-0">
          <div className="w-36 h-1 bg-slate-400/40 rounded-full"></div>
        </div>
      </div>
    );
  }

  // Desktop Device Frame
  return (
    <div className="w-full h-full max-w-full bg-slate-950 rounded-2xl shadow-2xl border-2 border-slate-800 flex flex-col overflow-hidden transition-all duration-300">
      {/* Desktop Browser Top Bar */}
      <div className="bg-slate-900 px-3.5 py-2 border-b border-slate-800 flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
        </div>
        <div className="flex-1 bg-slate-950 rounded-lg px-3 py-1 text-[11px] text-slate-300 font-mono flex items-center gap-2 border border-slate-800/80">
          <svg className="w-3 h-3 text-emerald-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span className="truncate">
            https://checkout.subscale.io/pay?plan={planTier.toLowerCase()}&amp;cur={currency}
          </span>
        </div>
        <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
          Live Preview
        </span>
      </div>

      {/* Desktop Viewport */}
      <div className="flex-1 w-full h-full overflow-hidden bg-slate-100 dark:bg-slate-950">
        {children}
      </div>
    </div>
  );
};
